<template>
  <div class="image-upload">
    <div class="upload-area" 
         :class="{ 
           'drag-over': isDragOver, 
           'has-image': imageUrl,
           'uploading': isUploading 
         }"
         @click="triggerFileInput"
         @dragover.prevent="handleDragOver"
         @dragleave.prevent="handleDragLeave"
         @drop.prevent="handleDrop">
      
      <!-- 图片预览 -->
      <div v-if="imageUrl && !isUploading" class="image-preview">
        <img :src="imageUrl" :alt="fileName" class="preview-image" />
        <div class="image-overlay">
          <div class="image-info">
            <span class="file-name">{{ fileName }}</span>
            <span class="file-size">{{ formatFileSize(fileSize) }}</span>
          </div>
          <div class="image-actions">
            <button @click.stop="removeImage" class="remove-btn" title="删除图片">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
            </button>
            <button @click.stop="triggerFileInput" class="change-btn" title="更换图片">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><polyline points="21 3 21 9 15 9"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- 上传中状态 -->
      <div v-else-if="isUploading" class="uploading-state">
        <div class="upload-spinner"></div>
        <div class="upload-text">
          <p>上传中...</p>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <span class="progress-text">{{ uploadProgress }}%</span>
        </div>
      </div>

      <!-- 默认上传区域 -->
      <div v-else class="upload-placeholder">
        <div class="upload-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
        </div>
        <div class="upload-text">
          <p class="primary-text">点击上传图片</p>
          <p class="secondary-text">或拖拽图片到此处</p>
          <p class="hint-text">支持 JPG、PNG、GIF 格式，最大 {{ maxSizeMB }}MB</p>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件输入 -->
    <input 
      ref="fileInput"
      type="file" 
      accept="image/*"
      @change="handleFileSelect"
      style="display: none"
    />

    <!-- 错误提示 -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { uploadFile } from '../api/index';

export default {
  name: 'ImageUpload',
  props: {
    // 当前图片URL
    value: {
      type: String,
      default: ''
    },
    // 最大文件大小（MB）
    maxSize: {
      type: Number,
      default: 5
    },
    // 图片分类
    category: {
      type: String,
      default: 'image'
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      isDragOver: false,
      isUploading: false,
      uploadProgress: 0,
      errorMessage: '',
      fileName: '',
      fileSize: 0,
      imageUrl: this.value
    };
  },
  
  computed: {
    maxSizeMB() {
      return this.maxSize;
    }
  },
  
  watch: {
    value(newVal) {
      this.imageUrl = newVal;
    }
  },
  
  methods: {
    // 触发文件选择
    triggerFileInput() {
      if (this.disabled || this.isUploading) return;
      this.$refs.fileInput.click();
    },
    
    // 处理文件选择
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadImage(file);
      }
      // 清空input值，允许重复选择同一文件
      event.target.value = '';
    },
    
    // 处理拖拽悬停
    handleDragOver() {
      if (this.disabled || this.isUploading) return;
      this.isDragOver = true;
    },

    // 处理拖拽离开
    handleDragLeave() {
      this.isDragOver = false;
    },
    
    // 处理文件拖放
    handleDrop(event) {
      if (this.disabled || this.isUploading) return;
      this.isDragOver = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadImage(files[0]);
      }
    },
    
    // 上传图片
    async uploadImage(file) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        this.showError('请选择图片文件');
        return;
      }

      // 验证文件大小
      const maxSizeBytes = this.maxSize * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        this.showError(`文件大小不能超过 ${this.maxSize}MB`);
        return;
      }

      this.clearError();
      this.isUploading = true;
      this.uploadProgress = 0;
      this.fileName = file.name;
      this.fileSize = file.size;

      // 如果文件较大，尝试压缩
      let fileToUpload = file;
      if (file.size > 1024 * 1024) { // 大于1MB时压缩
        try {
          fileToUpload = await this.compressImage(file);
          console.log(`图片压缩: ${this.formatFileSize(file.size)} -> ${this.formatFileSize(fileToUpload.size)}`);
        } catch (error) {
          console.warn('图片压缩失败，使用原图:', error);
          fileToUpload = file;
        }
      }
      
      try {
        const response = await uploadFile(fileToUpload, {
          category: this.category,
          onProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          }
        });

        if (response.data && response.data.code === 1) {
          const data = response.data.data;
          this.imageUrl = data.fullurl || data.url;
          this.fileName = data.name || file.name;
          this.fileSize = data.size || file.size;

          // 触发父组件更新
          this.$emit('input', data.url); // 存储相对路径
          this.$emit('change', {
            url: data.url,
            fullurl: data.fullurl,
            name: data.name,
            size: data.size,
            type: data.type
          });

        } else {
          throw new Error(response.data?.msg || '上传失败');
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        this.showError('上传失败: ' + (error.message || '未知错误'));
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },

    // 压缩图片
    compressImage(file, quality = 0.8, maxWidth = 1920, maxHeight = 1080) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          // 计算压缩后的尺寸
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;

          // 绘制压缩后的图片
          ctx.drawImage(img, 0, 0, width, height);

          // 转换为Blob
          canvas.toBlob((blob) => {
            // 创建新的File对象
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          }, file.type, quality);
        };

        img.onerror = () => {
          resolve(file); // 压缩失败时返回原文件
        };

        img.src = URL.createObjectURL(file);
      });
    },
    
    // 删除图片
    removeImage() {
      this.imageUrl = '';
      this.fileName = '';
      this.fileSize = 0;
      this.clearError();
      this.$emit('input', '');
      this.$emit('change', null);
    },
    
    // 显示错误
    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.clearError();
      }, 5000);
    },
    
    // 清除错误
    clearError() {
      this.errorMessage = '';
    },
    
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.image-upload {
  width: 100%;
}

.upload-area {
  border: 1px dashed var(--border);
  border-radius: 8px;
  background: var(--surface);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: var(--brand);
  background: var(--brand-soft);
}

.upload-area.drag-over {
  border-color: var(--brand);
  background: var(--brand-soft);
  transform: scale(1.02);
}

.upload-area.has-image {
  border: none;
  background: transparent;
  padding: 0;
}

.upload-area.uploading {
  border-color: var(--brand);
  background: var(--brand-soft);
}

/* 上传占位符 */
.upload-placeholder {
  padding: 28px 16px;
  text-align: center;
}

.upload-icon {
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  color: var(--brand);
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon svg {
  width: 100%;
  height: 100%;
}

.upload-text .primary-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 4px 0;
}

.upload-text .secondary-text {
  font-size: 12px;
  color: var(--text-dim);
  margin: 0 0 4px 0;
}

.upload-text .hint-text {
  font-size: 11px;
  color: var(--text-dim);
  margin: 0;
}

/* 图片预览 */
.image-preview {
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0) 70%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.image-info {
  color: white;
  text-align: left;
}

.file-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  opacity: 0.8;
}

.image-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.remove-btn,
.change-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-btn svg,
.change-btn svg {
  width: 14px;
  height: 14px;
}

.remove-btn:hover {
  background: rgba(239, 68, 68, 0.8);
}

.change-btn:hover {
  background: rgba(59, 130, 246, 0.8);
}

/* 上传中状态 */
.uploading-state {
  padding: 40px 20px;
  text-align: center;
}

.upload-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.uploading-state .upload-text p {
  font-size: 16px;
  color: var(--text);
  margin: 0 0 16px 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--surface-2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: var(--brand);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-text {
  font-size: 14px;
  color: var(--text-dim);
  font-weight: 500;
}

/* 错误提示 */
.error-message {
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #ef4444;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .upload-placeholder {
    padding: 30px 15px;
  }
  
  .upload-icon {
    width: 36px;
    height: 36px;
  }
  
  .image-preview {
    height: 150px;
  }
}
</style>
